import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { setPortalView } from "@/lib/portalApi";
import { SectionCard, ActionButton, StatusBadge } from "@/components/shared";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STATUS_TONES = {
  none: "neutral", invitation_sent: "info", active: "success",
  activated: "success",  // legacy alias — normalised to "active" in backend; handled defensively here
  disabled: "neutral", archived: "neutral",
};
const STATUS_LABELS = {
  none: "Not Invited", invitation_sent: "Invitation Sent",
  active: "Active", activated: "Active",  // legacy alias
  disabled: "Disabled", archived: "Archived",
};

function Stat({ label, value }) {
  return <div className="bg-slate-50 rounded-lg p-3"><div className="text-xs text-slate-500">{label}</div><div className="text-lg font-semibold text-slate-800 mt-0.5">{value}</div></div>;
}

export default function PortalAdminCard({ contactId, contactEmail, isAdmin }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(null);
  const [pvOpen, setPvOpen] = useState(false);
  const [pvMode, setPvMode] = useState("preview");
  const [pvReason, setPvReason] = useState("");
  const [pvBusy, setPvBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/portal/admin/${contactId}`); setInfo(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, label, key) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/portal/admin/${contactId}/${path}`);
      toast.success(data.message || `${label} done`);
      if (data.activation_url) setLink(data.activation_url.startsWith("http") ? data.activation_url : `${window.location.origin}${data.activation_url}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Could not ${label.toLowerCase()}`); }
    finally { setBusy(""); }
  };

  const startPortalView = async () => {
    if (pvMode === "interactive" && pvReason.trim().length < 3) {
      toast.error("A reason is required for Interactive Portal View.");
      return;
    }
    setPvBusy(true);
    try {
      const { data } = await api.post("/portal-view/sessions", {
        client_id: contactId, mode: pvMode, reason: pvReason.trim() || undefined,
      });
      // Store the SCOPED session (never a client login) so the portal tab renders in view mode.
      setPortalView({
        token: data.token, mode: data.mode, session_id: data.session_id,
        subject: data.subject, actor: data.actor,
        reason: pvReason.trim() || undefined,
        deadline_ms: data.absolute_expires_at ? new Date(data.absolute_expires_at).getTime() : null,
        // Return context: capture the originating staff route (including tab) so Exit
        // can navigate back to the exact workspace the staff launched from.
        returnTo: window.location.href,
        client_id: contactId,
      });
      window.open("/portal", "_blank");
      toast.success(`Opened ${pvMode === "interactive" ? "Interactive" : "Read-only"} Portal View in a new tab.`);
      setPvOpen(false);
      setPvReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open Portal View");
    } finally { setPvBusy(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(link); toast.success("Activation link copied"); };

  if (loading) return <SectionCard title="Secure Portal" icon="ShieldCheck"><div className="py-6 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div></SectionCard>;

  const status = info?.portal_status || "none";
  const notInvited = status === "none";
  const isActive = status === "active" || status === "activated";  // "activated" is legacy alias

  return (
    <SectionCard title="Secure Portal" icon="ShieldCheck"
      action={<StatusBadge status={status} map={STATUS_TONES} labels={STATUS_LABELS} />}>
      <div className="space-y-4" data-testid="portal-admin-card">
        {!contactEmail && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Icons.AlertTriangle className="h-4 w-4" /> Add an email to this contact before enabling the portal.
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Invited" value={info?.invited_at ? new Date(info.invited_at).toLocaleDateString() : "—"} />
          <Stat label="Activated" value={info?.activated_at ? new Date(info.activated_at).toLocaleDateString() : "—"} />
          <Stat label="Last Login" value={info?.last_login_at ? new Date(info.last_login_at).toLocaleDateString() : "—"} />
          <Stat label="Unread" value={info?.unread_messages ?? 0} />
        </div>

        {link && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs text-blue-700 mb-1.5 font-medium">Activation link (email integration sends this automatically when enabled):</div>
            <div className="flex items-center gap-2">
              <input readOnly value={link} className="flex-1 bg-white border border-blue-200 rounded px-2 py-1.5 text-xs text-slate-600" data-testid="portal-activation-link" />
              <button onClick={copyLink} className="p-1.5 rounded bg-blue-600 text-white"><Icons.Copy className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {notInvited && <ActionButton icon="Send" loading={busy === "enable"} disabled={!contactEmail} onClick={() => act("enable", "Enable Portal", "enable")} data-testid="portal-enable">Enable Portal</ActionButton>}
          {status === "invitation_sent" && <ActionButton icon="RefreshCw" variant="secondary" loading={busy === "resend"} onClick={() => act("resend", "Resend Invitation", "resend")} data-testid="portal-resend">Resend Invite</ActionButton>}
          {isActive && <ActionButton icon="KeyRound" variant="secondary" loading={busy === "reset"} onClick={() => act("reset", "Reset Password", "reset")} data-testid="portal-reset">Reset Password</ActionButton>}
          {isAdmin && isActive && <ActionButton icon="Eye" variant="secondary" onClick={() => setPvOpen(true)} data-testid="portal-view-open">Open Portal View</ActionButton>}
          {!notInvited && status !== "disabled" && <ActionButton icon="Ban" variant="ghost" loading={busy === "disable"} onClick={() => act("disable", "Disable Portal", "disable")} data-testid="portal-disable">Disable</ActionButton>}
          {status === "disabled" && <ActionButton icon="Power" loading={busy === "enable"} onClick={() => act("enable", "Re-enable Portal", "enable")} data-testid="portal-reenable">Re-enable</ActionButton>}
        </div>
        <p className="text-[11px] text-slate-400">The portal expands automatically as the relationship grows — no new logins or separate portals. Permissions follow the lifecycle (lead → active → inactive).</p>
      </div>

      <Dialog open={pvOpen} onOpenChange={setPvOpen}>
        <DialogContent data-testid="portal-view-dialog">
          <DialogHeader>
            <DialogTitle>Open Portal View</DialogTitle>
            <DialogDescription>
              You will view this client&rsquo;s portal as yourself — you are never logged in as the client.
              Everything is audited.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={pvMode} onValueChange={setPvMode} className="space-y-2">
            <label htmlFor="pv-preview" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${pvMode === "preview" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
              <RadioGroupItem value="preview" id="pv-preview" className="mt-1" data-testid="portal-view-mode-preview" />
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><Icons.Eye className="h-4 w-4 text-amber-600" /> Read-Only Preview</div>
                <div className="text-xs text-slate-500">See exactly what the client sees. All actions are disabled.</div>
              </div>
            </label>
            <label htmlFor="pv-interactive" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${pvMode === "interactive" ? "border-rose-500 bg-rose-50" : "border-slate-200"}`}>
              <RadioGroupItem value="interactive" id="pv-interactive" className="mt-1" data-testid="portal-view-mode-interactive" />
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><Icons.PenLine className="h-4 w-4 text-rose-600" /> Interactive (Act on Behalf)</div>
                <div className="text-xs text-slate-500">Assist the client with governed actions. Signatures, contractual acceptance, attestations, identity verification and payments remain blocked.</div>
              </div>
            </label>
          </RadioGroup>
          <div className="space-y-1.5">
            <Label htmlFor="pv-reason" className="text-xs">
              Reason {pvMode === "interactive" ? <span className="text-rose-600">(required)</span> : "(optional)"}
            </Label>
            <Textarea id="pv-reason" value={pvReason} onChange={(e) => setPvReason(e.target.value)}
              placeholder="e.g. Helping the client complete their organizer over a support call"
              rows={2} data-testid="portal-view-reason" />
          </div>
          <DialogFooter>
            <ActionButton variant="ghost" onClick={() => setPvOpen(false)} data-testid="portal-view-cancel">Cancel</ActionButton>
            <ActionButton icon="ExternalLink" loading={pvBusy} onClick={startPortalView} data-testid="portal-view-start">
              Open {pvMode === "interactive" ? "Interactive View" : "Preview"}
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
